
import { List, Input, Avatar, Spin, Pagination, DatePicker } from "antd";
import { SearchOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useState, useMemo, useEffect } from "react";
import { useDebounce } from "../../../../../core/hooks/useDebounce";
import api from "../../../../../core/axios/axiosInstance";
import { clearEmptyDraftChats } from "../../../../../core/data/redux/slices/WabaChatSlice";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { showToast } from "../../../../../core/data/redux/slices/ToastSlice";
import ListSkeleton from "../../../../../core/common/ListSkeleton";

const { RangePicker } = DatePicker;

const ChatInfoTip = ({ isHistoryPage }) => {
  const [show, setShow] = useState(false);
  const text = isHistoryPage
    ? "Contacts who have not replied in the last 24 hours appear here"
    : "Contacts who have replied in the last 24 hours appear here";
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <InfoCircleOutlined
        style={{ fontSize: 13, color: "#9ca3af", cursor: "pointer" }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      />
      {show && (
        <span style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          left: "50%",
          transform: "translateX(-50%)",
          background: "#1f2937",
          color: "#fff",
          fontSize: 11,
          padding: "6px 10px",
          borderRadius: 6,
          whiteSpace: "normal",
          width: 220,
          zIndex: 100,
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          lineHeight: 1.5,
          textAlign: "center",
        }}>
          {text}
          <span style={{
            position: "absolute",
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            borderBottom: "5px solid #1f2937",
          }} />
        </span>
      )}
    </span>
  );
};


const ChatList = ({ data: localChats, activeId, onSelect, onNewChat, loading, chatPage, setChatPage, totalCount, chatLimit, setChatLimit, readFilter, setReadFilter, dateRange, setDateRange }) => {
  const [search, setSearch] = useState("");
  const dispatch = useDispatch()
  const location = useLocation()
  const debouncedSearch = useDebounce(search, 300);
  const [apiContacts, setApiContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);

  const isHistoryPage =
    location.pathname === "/whatsapp/history" ||
    location.pathname === "/agent/whatsapp/history" ||
    location.pathname === "/admin/whatsapp/history";

  const isAdminHistoryPage = location.pathname === "/admin/whatsapp/history";

  const fetchContactsAndLeads = async (searchQuery = "") => {
    if (!searchQuery.trim()) return setApiContacts([]);

    try {
      setIsLoadingContacts(true);

      if (isAdminHistoryPage) {
        // Superadmin: search company admins + agents
        const response = await api.post("/superAdmin/allCompanyAdmin", { search: searchQuery, limit: 20 });
        const list = response.data?.data || [];
        const transformed = list.map((item) => {
          const name = `${item.firstname || ""} ${item.lastname || ""}`.trim() || item.email;
          const phone = item.phonenumbers?.[0]
            ? `${item.phonenumbers[0].countryCode || ""}${item.phonenumbers[0].number}`
            : item.telephone || "";
          return {
            id: `${item._id}`,
            name,
            phone,
            category: item.role === "companyAdmin" ? "Company Admin" : "Agent",
            lastMessage: "",
            unread: 0,
            image: "",
            isWabaChat: false,
          };
        });
        setApiContacts(transformed);
      } else {
        // Company admin / agent: search contacts & leads
        const response = await api.get("/getAllContactsOrLeads/ForWhatsapp", { params: { search: searchQuery } });
        const transformed = response.data.data.map((item) => {
          const name = `${item.firstname || ""} ${item.lastname || ""}`.trim();
          const phone = item.phoneNumbers?.[0]
            ? `+${item.phoneNumbers[0].countryCode}${item.phoneNumbers[0].number}`
            : "";
          return {
            id: `${item.id}`,
            name,
            phone,
            category: item.category,
            lastMessage: "",
            unread: 0,
            image: "",
            isWabaChat: item.isWabaChat,
          };
        });
        setApiContacts(transformed);
      }
    } catch (error) {
      console.error(error.response?.data?.message || "Error fetching search results");
      setApiContacts([]);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  useEffect(() => {
    fetchContactsAndLeads(debouncedSearch);
  }, [debouncedSearch]);

  const filteredLocalChats = useMemo(() => {
    let chats = localChats;

    const query = debouncedSearch?.trim()?.toLowerCase();
    if (query) {
      chats = chats.filter(
        (item) =>
          item?.chatName?.toLowerCase().includes(query) ||
          item?.originalName?.toLowerCase().includes(query) ||
          item?.phone?.toLowerCase().includes(query) ||
          item?.from?.toLowerCase().includes(query)
      );
    }

    if (readFilter === "unread") chats = chats.filter((c) => (c.unread || 0) > 0);
    if (readFilter === "read")   chats = chats.filter((c) => !c.unread || c.unread === 0);

    if (isHistoryPage && dateRange?.[0] && dateRange?.[1]) {
      const from = dateRange[0].startOf("day").valueOf();
      const to   = dateRange[1].endOf("day").valueOf();
      chats = chats.filter((c) => {
        const t = c.lastMessageTime ? new Date(c.lastMessageTime).getTime() : null;
        return t && t >= from && t <= to;
      });
    }

    const sorted = [...chats].sort((a, b) => {
      const ta = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : Infinity;
      const tb = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : Infinity;
      return tb - ta;
    });
    return sorted;
  }, [localChats, debouncedSearch, readFilter, dateRange, isHistoryPage]);

  return (
    <div className="chat-list">

      {/* ── Fixed header: title + search + filters ── */}
      <div style={{ flexShrink: 0 }}>
        <div className="chat-list-header" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <strong>{isHistoryPage ? "WhatsApp History" : "WhatsApp Chat"}</strong>
          <ChatInfoTip isHistoryPage={isHistoryPage} />
        </div>

        {/* Search */}
        <div style={{ padding: "10px 10px 6px" }}>
          <Input
            prefix={<SearchOutlined style={{ color: "#adb5bd", fontSize: 13 }} />}
            placeholder={isHistoryPage ? "Search history, contacts & leads" : "Search chats by name or number"}
            style={{ borderRadius: 20, background: "#f0f2f5", border: "1px solid #e5e7eb", height: 36, fontSize: 13 }}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (e.target.value.trim() === " ") dispatch(clearEmptyDraftChats());
            }}
          />
        </div>

        {/* Filters row */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 10px 10px" }}>
          {[
            { key: "all",    label: "All",    color: "#3C2371" },
            { key: "unread", label: "Unread", color: "#3C2371" },
            { key: "read",   label: "Read",   color: "#3C2371" },
          ].map(({ key, label, color }) => (
            <button
              key={key}
              onClick={() => setReadFilter(key)}
              style={{
                flexShrink: 0,
                padding: "4px 10px",
                borderRadius: 20,
                border: `1.5px solid ${readFilter === key ? color : "#e5e7eb"}`,
                background: readFilter === key ? color : "#fff",
                color: readFilter === key ? "#fff" : "#6b7280",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
                outline: "none",
              }}
            >
              {label}
            </button>
          ))}
          {isHistoryPage && (
            <RangePicker
              size="small"
              style={{ flex: 1, minWidth: 0, borderRadius: 20, height: 28, fontSize: 11 }}
              value={dateRange}
              onChange={(dates) => setDateRange(dates || [null, null])}
              allowClear
              placeholder={["From", "To"]}
              format="DD MMM"
            />
          )}
        </div>
      </div>

      {/* ── Scrollable middle: chat list + contact search results ── */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {loading ? (
          <ListSkeleton rows={8} avatarSize={42} />
        ) : (
        <List
          itemLayout="horizontal"
          dataSource={filteredLocalChats}
          renderItem={(item) => (
            <List.Item
              className={`chat-item ${item.id === activeId ? "active" : ""}`}
              onClick={() => {
                console.log(item, "hjgfhvjbknljhjgf");
                onSelect(item)
              }}
            >
              <div className="wa-chat-item-inner">
                <Avatar
                  className="chat-avatar"
                  size={42}
                  src={item.image ? item.image : undefined}
                >
                  {(item?.chatName || item?.originalName || item?.from || "?").charAt(0)}
                </Avatar>
                <div className="wa-chat-body">
                  <div className="wa-chat-name-row">
                    <span className="wa-chat-name">
                      {item?.chatName || item.originalName || item?.from}
                    </span>
                    {item?.chatCompany && (
                      <span className="wa-chat-company">({item.chatCompany})</span>
                    )}
                  </div>
                  <span className="wa-chat-preview">
                    {typeof item.lastMessage === "string"
                      ? item.lastMessage
                      : item.lastMessage?.body || "No messages yet"}
                  </span>
                </div>
                <div className="wa-chat-item-right">
                  <span className="wa-chat-time">
                    {item?.lastMessageTime
                      ? item.isWabaChat
                        ? new Date(item.lastMessageTime).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : new Date(item.lastMessageTime).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                      : ""}
                  </span>
                  {item.unread > 0 && (
                    <span className="wa-unread-badge">{item.unread}</span>
                  )}
                </div>
              </div>
            </List.Item>
          )}
        />
        )}
        {isHistoryPage && <>
          {isLoadingContacts && debouncedSearch.trim() && (
            <div style={{ textAlign: "center", padding: 10 }}>
              <Spin size="small" /> Loading contacts...
            </div>
          )}
          {!isLoadingContacts && apiContacts.length > 0 && (
            <>
              <div style={{ padding: "5px 10px", fontSize: 12, color: "#888" }}>
                {isAdminHistoryPage ? "Company Admins / Agents" : "From Contacts / Leads"}
              </div>
              <List
                itemLayout="horizontal"
                dataSource={apiContacts}
                renderItem={(item) => (

                  <List.Item
                    className={`chat-item ${item.id === activeId ? "active" : ""}`}
                    onClick={() => {
                      console.log(item.phone, "phone number of clicked item");
                      if (!item.phone) {
                        dispatch(
                          showToast({
                            message: "Selected Contact does not have a Phone Number",
                            variant: "danger"
                          })
                        );
                        return;
                      }
                      onNewChat(item)
                    }}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          className="chat-avatar"
                          size={42}
                          src={item.image ? item.image : undefined}
                        >
                          {item.name ? item.name.charAt(0) : "?"}
                        </Avatar>
                      }
                      title={
                        <span className="chat-name">
                          {item.name}{" "}
                          {item.category && (
                            <span
                              style={{ fontSize: 11, color: "#888", marginLeft: 4 }}
                            >
                              ({item.category})
                            </span>
                          )}
                        </span>
                      }
                      description={<span className="chat-last-message">{item.phone || ""}</span>}
                    />
                  </List.Item>
                )}
              />
            </>
          )}
        </>}
      </div>

      {/* ── Fixed footer: pagination ── */}
      <div style={{ flexShrink: 0, padding: "10px 16px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "center" }}>
        <Pagination
          current={chatPage}
          total={totalCount}
          pageSize={chatLimit}
          onChange={(page, pageSize) => {
            setChatPage(page);
            setChatLimit(pageSize);
          }}
          size="small"
          showSizeChanger={true}
          pageSizeOptions={["5", "10", "20", "50"]}
          showQuickJumper={Math.ceil(totalCount / chatLimit) > 1}
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} chats`}
          style={{ margin: "10px 0", display: "flex", justifyContent: "center", gap: "4px" }}
        />
      </div>

    </div>
  );
};

export default ChatList;